import { Head } from '@inertiajs/react'
import { useState } from 'react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import { ChartBarIcon } from '@heroicons/react/24/outline'

interface ReportFormProps {
  slug: string
  title: string
  subtitle?: string
  submitUrl: string
}

// Generic date-range "pick start + end + export" form, used by every
// legacy report that just needs two dates and POSTs to an .xlsx/.pdf
// endpoint. Native form POST with target="_blank" so the file downloads
// in a new tab and the user stays on this page.
//
// Inputs use readOnly (not disabled) while submitting — `disabled` would
// strip values from the POST body. Same lesson the Login page taught us.
export default function ReportForm({ title, subtitle, submitUrl }: ReportFormProps) {
  const [submitting, setSubmitting] = useState(false)
  const csrf =
    (typeof document !== 'undefined' &&
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content) ||
    ''

  // Re-enable controls after a beat so a slow export can still be
  // re-triggered if the user changes the date range and clicks Export
  // again. The form opens in a new tab anyway, so this page is reusable.
  const handleSubmit = () => {
    setSubmitting(true)
    setTimeout(() => setSubmitting(false), 3000)
  }

  return (
    <>
      <Head title={title} />
      <PageHeader
        title={title}
        subtitle={subtitle}
        icon={<ChartBarIcon className="h-5 w-5" />}
      />

      <form
        action={submitUrl}
        method="POST"
        target="_blank"
        onSubmit={handleSubmit}
        className="max-w-md space-y-4"
        data-testid="report-form"
      >
        <input type="hidden" name="authenticity_token" value={csrf} />

        <div>
          <label htmlFor="start_date" className="block text-sm font-medium text-zinc-900 dark:text-white">
            Start Date
          </label>
          <input
            id="start_date"
            type="date"
            name="start_date"
            required
            readOnly={submitting}
            className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 read-only:cursor-not-allowed read-only:bg-zinc-50 read-only:text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
          />
        </div>

        <div>
          <label htmlFor="end_date" className="block text-sm font-medium text-zinc-900 dark:text-white">
            End Date
          </label>
          <input
            id="end_date"
            type="date"
            name="end_date"
            required
            readOnly={submitting}
            className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 read-only:cursor-not-allowed read-only:bg-zinc-50 read-only:text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
          />
        </div>

        <div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-indigo-400"
          >
            {submitting ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </form>
    </>
  )
}

ReportForm.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
